/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect } from 'react';
import { Breakpoint, Select, Stack, Text, Button, useBreakpoint } from '@moda/om';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Link } from '../../components/Link';
import { PageMeta } from '../../components/PageMeta';
import { ContactUs } from '../../components/ContactUs';
import { Careers } from '../../components/Careers';

import './AboutPage.scss';

const SCROLL_INTO_VIEW_TIMEOUT = 100;

const options = [
  { value: '#careers', label: 'Careers' },
  { value: '#openings', label: 'Explore Current Openings' },
  { value: '#brand', label: 'Our Brand' },
  { value: '#founder', label: 'Our Founder' },
  { value: '#affiliates', label: 'Affiliates' },
  { value: '#contact', label: 'Contact Us' }
];

export const AboutPage: React.FC = () => {
  const { vertical = 'women' } = useParams<{ vertical?: string }>();
  const location = useLocation();
  const history = useHistory();

  const handleChange = useCallback((value: string) => history.push(`${value}`), [history]);

  useEffect(() => {
    const element = document.getElementById(location.hash.replace('#', ''));
    if (!element) return;

    setTimeout(
      () => element.scrollIntoView({ block: 'start', inline: 'start' }),
      SCROLL_INTO_VIEW_TIMEOUT
    );
  }, [location]);

  const { matches: isMobile } = useBreakpoint({ lt: 'sm' });

  return (
    <>
      <PageMeta title="About" />

      <div className="AboutPage">
        <div className="AboutPage__desktop-nav">
          <Breakpoint gt="md">
            <div className="AboutPage__nav-items">
              <div>
                <a className="AboutPage__nav-link" href="#careers">
                  CAREERS
                </a>
              </div>
              <div>
                <a className="AboutPage__nav-link" href="#openings">
                  EXPLORE CURRENT OPENINGS
                </a>
              </div>
              <div>
                <a className="AboutPage__nav-link" href="#brand">
                  OUR BRAND
                </a>
              </div>
              <div>
                <a className="AboutPage__nav-link" href="#founder">
                  OUR FOUNDER
                </a>
              </div>
              <div>
                <a className="AboutPage__nav-link" href="#affiliates">
                  AFFILIATES
                </a>
              </div>
              <div>
                <a className="AboutPage__nav-link" href="#contact">
                  CONTACT US
                </a>
              </div>
              <div>
                <a
                  className="AboutPage__nav-link"
                  href="https://help.modaoperandi.com/hc/en-us"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  FAQS
                </a>
              </div>
            </div>
          </Breakpoint>
        </div>
        <div className="AboutPage__mobile-nav">
          <Breakpoint lt="md">
            <Select
              className="AboutPage__nav-select"
              label="Jump To"
              onChange={handleChange}
              options={options}
            />
          </Breakpoint>
        </div>
        <Stack space={8} className="AboutPage__section-careers">
          <a className="AboutPage__empty" id="careers">
            &nbsp;
          </a>
          <Stack space={4} className="AboutPage__careers-info">
            <Text className="AboutPage__section-title" treatment="h1" family="serif" as="h1">
              Join the Team
            </Text>
            <Text treatment="body1" family="serif">
              Moda Operandi is the global destination for luxury fashion, providing bespoke access,
              brand discovery, and unparalleled curation.
            </Text>
            <Stack space={2}>
              <Text treatment="h4" family="serif" as="h4">
                What sets us apart?
              </Text>
              <Text treatment="body1" family="serif">
                At Moda you will have the unique opportunity to innovate, inspire others, and engage
                with fashion at the highest level.
              </Text>
            </Stack>
            <Stack space={3}>
              <Text treatment="h4" family="serif" as="h4">
                Our values
              </Text>
              <Stack space={1}>
                <Text treatment="h6" family="serif" as="h6">
                  <strong>Caring</strong>
                </Text>
                <Text treatment="body1" family="serif">
                  We are mindful of the needs of others. We treat others, the brand, and ourselves
                  with respect. We actively listen and respond with compassion.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text treatment="h6" family="serif" as="h6">
                  <strong>Creative</strong>
                </Text>
                <Text treatment="body1" family="serif">
                  We are entrepreneurial, innovative, and adaptable. We recognize that a calm
                  environment, work-life balance, and effective prioritization are critical to
                  generate good ideas.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text treatment="h6" family="serif" as="h6">
                  <strong>Client Centric</strong>
                </Text>
                <Text treatment="body1" family="serif">
                  We are customer-focused in everything we do. Both externally (i.e.: our clients)
                  and internally (i.e.: your colleagues), we strive for best-in-class customer
                  experience.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text treatment="h6" family="serif" as="h6">
                  <strong>Positivity</strong>
                </Text>
                <Text treatment="body1" family="serif">
                  We believe in optimism and resilience. We see challenges as opportunities. We seek
                  to elevate and encourage others, and always assume positive intent.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text treatment="h6" family="serif" as="h6">
                  <strong>Striving for Excellence</strong>
                </Text>
                <Text treatment="body1" family="serif">
                  We are one team, and are committed to excel together. Alignment is key to success.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text treatment="body1" family="serif">
                  <strong>Please note:</strong> all communications from Moda, including regarding an
                  application or an offer of employment, will be from a modaoperandi.com email
                  address. Emails that come from any domains other than modaoperandi.com are not
                  from Moda Operandi and may be fraudulent.
                </Text>
              </Stack>
            </Stack>
          </Stack>
          <a
            className="AboutPage__empty"
            id="openings"
            style={{ top: isMobile ? '800px' : '475px' }}
          >
            &nbsp;
          </a>
          <Careers />
        </Stack>
        <div className="AboutPage__section-our-brand">
          <a className="AboutPage__empty" id="brand">
            &nbsp;
          </a>
          <div className="AboutPage__image">
            <img src="/dist/public/branding_bg.jpg" alt="brand" loading="lazy" />
          </div>
          <Stack space={6} className="AboutPage__side">
            <Text treatment="h2" family="serif" as="h2">
              Our Brand
            </Text>
            <Text treatment="body1">
              In homage to the history of couturiers who showcased their creations in trunks to
              elite clientele, Moda Operandi is the only online retailer to invite clients to{' '}
              <Link className="AboutPage__link" to={`${vertical}/trunkshows`}>
                PREORDER
              </Link>{' '}
              next-season’s looks straight from the runway. And for those who can’t wait, we offer
              an expertly curated in-season{' '}
              <Link className="AboutPage__link" to={vertical}>
                BOUTIQUE
              </Link>
              .
            </Text>
            <Text treatment="body1">
              The vibrant theatrics of fashion week come to life with our highly editorialized
              shopping experience. Spellbinding imagery and evocative language showcase our
              exceptional designer offerings, comprised of everything from premier fashion houses to
              emerging vanguard labels. Expect unprecedented access to stunning ready-to-wear,
              luxurious leather goods, and museum-worthy fine jewelry.
            </Text>
            <Text treatment="body1">
              Moda Operandi's dedicated{' '}
              <a
                className="AboutPage__link"
                href="https://anvela.cn/editorial/moda-private"
              >
                PRIVATE CLIENT ADVISORS
              </a>{' '}
              provide the most bespoke services in luxury shopping, attending to our client’s every
              need, from expert styling advice to tailored wardrobe recommendations. With a
              sophisticated suite of high-tech meets high-touch services, our seasoned team of
              fashion industry professionals is just a call or click away. We invite you to shop
              with us and see why the Moda Operandi experience is like no other.
            </Text>
          </Stack>
        </div>
        <div className="AboutPage__section-founder">
          <a className="AboutPage__empty" id="founder">
            &nbsp;
          </a>
          <Text treatment="h2" family="serif">
            Our Founder
          </Text>
          <img src="/dist/public/founder.png" alt="founder" loading="lazy" />
          <Text treatment="eyebrow">CO-FOUNDER</Text>
          <Text treatment="h4" family="serif">
            Lauren Santo Domingo
          </Text>
          <Text treatment="body1" family="serif">
            "Our customer knows that any designer we feature, we believe in, and she’ll be getting
            the most up to date fashion."
          </Text>
          <Text treatment="body1" family="serif">
            One of the most authoritative figures on New York’s fashion circuit, a muse to feted
            fashion designers such as Proenza Schouler and featured on Vanity Fair's International
            Best Dressed List, Lauren Santo Domingo co-founded Moda Operandi in 2011. Raised in
            Greenwich, Connecticut, she received her education at the University of Southern
            California and began her career at Vogue, where she is now a contributing editor. She
            brings her extensive experience and industry relationships to Moda Operandi.
          </Text>
        </div>
        <Stack space={6} className="AboutPage__section-wrapper">
          <a className="AboutPage__empty" id="affiliates">
            &nbsp;
          </a>
          <Stack space={6} className="AboutPage__section-affiliates">
            <Text treatment="h2" family="serif">
              Affiliates Partners
            </Text>
            <Text className="AboutPage__text" treatment="body1" family="serif">
              Moda Operandi is thrilled to partner with websites and blogs on the vanguard of
              innovation in fashion, lifestyle, design, and beauty. As a member of the program,
              please enjoy the benefits below:
            </Text>
            <div className="AboutPage__row">
              <div className="AboutPage__column">
                <div className="AboutPage__column-title">01</div>
                <div className="AboutPage__column-text">Competitive commission rates</div>
              </div>
              <div className="AboutPage__column">
                <div className="AboutPage__column-title">02</div>
                <div className="AboutPage__column-text">Performance-based incentives</div>
              </div>
              <div className="AboutPage__column">
                <div className="AboutPage__column-title">03</div>
                <div className="AboutPage__column-text">
                  First look at product images from the latest runway collections
                </div>
              </div>
              <div className="AboutPage__column">
                <div className="AboutPage__column-title">04</div>
                <div className="AboutPage__column-text">
                  Advance notice of Moda Operandi trunkshow dates
                </div>
              </div>
              <div className="AboutPage__column">
                <div className="AboutPage__column-title">05</div>
                <div className="AboutPage__column-text">Access to Moda Operandi ads</div>
              </div>
              <div className="AboutPage__column">
                <div className="AboutPage__column-title">06</div>
                <div className="AboutPage__column-text">A dedicated affiliate team</div>
              </div>
            </div>
            <Text className="AboutPage__text" treatment="body1" family="serif">
              How to Sign Up?
            </Text>
            <Text className="AboutPage__text" treatment="body1" family="serif">
              Complete the online application to become a member of the Moda Operandi Affiliate
              Program. You can also email us{' '}
              <a className="AboutPage__link" href="mailto:affiliates@modaoperandi.com">
                AFFILIATES@MODAOPERANDI.COM
              </a>
            </Text>
            <Button
              className="AboutPage__button"
              target="_blank"
              href="https://signup.cj.com/member/signup/publisher/?cid=5397322"
            >
              APPLY NOW
            </Button>
            <Text className="AboutPage__text" treatment="body2" family="serif">
              Join the Moda Operandi Affiliate Program.
            </Text>
          </Stack>
        </Stack>
        <Stack space={6} className="AboutPage__section-contact">
          <a className="AboutPage__empty" id="contact">
            &nbsp;
          </a>
          <Text treatment="h2" family="serif">
            Contact Us
          </Text>
          <ContactUs />
        </Stack>
      </div>
    </>
  );
};
